import { Injectable } from '@angular/core';
import { EventName, Events } from '../utils/events';
import { BaseProvider } from './base';
import { API } from './api';
import { ClassProvider } from './class';
import { ExportedPolygon } from './workspace/canvas';
import { DatasetProvider } from './dataset';
import { SettingsProvider } from './settings';
import { BrainBankErrorCode } from './error-code';

export interface UnprocessedClassCount {
    classIdentifier: string;
    numPolygons: number;
}

export interface ProcessedClassCount {
    numPolygons: number;
    classIdentifier: string;
    className: string;
    color: string;
}

export class TaggableAsset {
    id: string;
    name: string;
    prefix: string;
    url: string;
    thumb: string;
    classCounts: ProcessedClassCount[];
    metadata: any;
    type: string;
    polygons?: ExportedPolygon[];

    // Can be set by the workspace to keep track of current video frame
    currentFrame?: number;
}

export interface ClassCountsMap {
    [key: string]: number;
}

export interface AssetResults {
    assets: TaggableAsset[];
    total: number;
}

export interface SortOrder {
    sort: string | null;
    order: string | null;
}

export interface FramesMap {
    [key: number]: ExportedPolygon[];
}

export interface FramesResult {
    success: boolean;
    frames: FramesMap;
}

export const SHOW_ALL_LABEL = 'Show All';

@Injectable()
export class AssetProvider extends BaseProvider {
    private uploadModalOpen = false;
    private allLockedAssets: string[] = [];
    private assetResults: AssetResults;
    private currentAssetIndex = 0;
    private saving = false;
    private loading = false;

    constructor(
        private api: API,
        private classProvider: ClassProvider,
        private events: Events,
        private datasetProvider: DatasetProvider,
        private settingsProvider: SettingsProvider
    ) {
        super();
    }

    public async getPolygonsForAsset(assetIdentifier: string) {
        try {
            const polygons: FramesResult = await this.api.get(`assets/${assetIdentifier}/polygons`);

            return polygons;
        } catch (err) {
            throw err;
        }
    }

    public async getAssets(
        datasetIdentifier: string | null,
        limit?: number,
        offset?: number,
        sort?: string | null,
        order?: string | null,
        assets?: string[] | null,
        search?: string | null,
        tagged?: boolean | null,
        classIdentifiers?: string[] | null
    ): Promise<AssetResults> {
        const params: string[] = [];

        if (datasetIdentifier) {
            params.push(`datasetIdentifier=${datasetIdentifier}`);
        }
        if (limit) {
            params.push(`limit=${limit}`);
        }
        if (offset) {
            params.push(`offset=${offset}`);
        }
        if (sort) {
            params.push(`sort=${sort}`);
        }
        if (order) {
            params.push(`order=${order}`);
        }
        if (assets) {
            assets.forEach(asset => params.push(`assetIdentifiers[]=${asset}`));
        }
        if (search) {
            params.push(`search=${search}`);
        }
        if (tagged !== null && tagged !== undefined) {
            params.push(`tagged=${tagged}`);
        }
        if (classIdentifiers) {
            classIdentifiers.forEach(c => {
                params.push(`classes[]=${c}`);
            });
        }

        // tslint:disable-next-line: prefer-template
        const queryString = 'assets' + (params ? '?' + params.join('&') : '');

        try {
            this.loading = true;
            const result = await this.api.get(queryString);

            const TaggableAssets: TaggableAsset[] = result.message.assets.map(assetObject => {
                return this.convertAssetResponseObjectToTaggableAsset(assetObject);
            });

            this.assetResults = { assets: TaggableAssets, total: result.message.total };
            return this.assetResults;
        } catch (err) {
            throw err;
        } finally {
            this.loading = false;
        }
    }

    public async getAsset(assetIdentifier: string): Promise<TaggableAsset> {
        const result = await this.api.get(`assets?assetIdentifiers[]=${assetIdentifier}`);
        if (result.message && result.message.assets && result.message.assets.length > 0 && result.message.assets[0]) {
            return this.convertAssetResponseObjectToTaggableAsset(result.message.assets[0]);
        } else {
            throw new Error(`No asset found with identifier: ${assetIdentifier}`);
        }
    }

    public convertAssetResponseObjectToTaggableAsset(assetObject: any): TaggableAsset {
        const asset = new TaggableAsset();
        asset.metadata = assetObject.metadata;
        asset.type = assetObject.type;
        asset.id = assetObject.assetIdentifier;
        asset.name = assetObject.name;
        asset.prefix = assetObject.prefix;
        asset.url = assetObject.signedUrl;
        asset.thumb = assetObject.thumbSignedUrl;
        asset.classCounts = this.processClassCounts(assetObject.classCounts || []);

        return asset;
    }

    /**
     * Get the results of the latest request to the assets endpoint (i.e, getAssets())
     *
     * @returns {AssetResults}
     */
    public getAssetResults(): AssetResults {
        return this.assetResults;
    }

    public getCurrentAssetIndex(): number {
        return this.currentAssetIndex;
    }

    public setCurrentAssetIndex(assetIndex: number) {
        if (this.assetResults && assetIndex < this.assetResults.total && assetIndex >= 0) {
            this.currentAssetIndex = assetIndex;
        } else {
            // When user is allowed to click on the next asset (arrow), index is incremented, and can go past the range
            // In this case we will go to the first asset index
            this.currentAssetIndex = 0;
        }
    }

    public isUploadModalOpen(): boolean {
        return this.uploadModalOpen;
    }

    public isAssetSaving(): boolean {
        return this.saving;
    }

    public isAssetLoading(): boolean {
        return this.loading;
    }

    public isIdle(): boolean {
        return !this.loading && !this.saving;
    }

    public hasMultipleAssets(): boolean {
        return this.assetResults.assets && this.assetResults.total > 1;
    }

    public canGoToPreviousImage(): boolean {
        return this.isIdle() && this.hasMultipleAssets() && this.currentAssetIndex > 0;
    }

    public canGoToNextImage(): boolean {
        return this.isIdle() && this.hasMultipleAssets() && this.currentAssetIndex < this.assetResults.total - 1;
    }

    public canFinish(): boolean {
        return !this.assetResults.assets || this.getCurrentAssetIndex() >= this.assetResults.total - 1;
    }

    /**
     * Get the image offset from inside the dataset
     *
     * @params {string} imageIdentifier
     *
     * @returns {number}
     */
    public async getDatasetOffset(datasetIdentifier: string, imageIdentifier: string): Promise<number> {
        try {
            const sortOrder = await this.loadSortOrder(datasetIdentifier);

            const response = await this.api.get(
                `images/${imageIdentifier}/offset?sort=${sortOrder.sort}&order=${sortOrder.order}`
            );

            return response.message.offset;
        } catch (err) {
            console.warn('Unable to fetch most recently touched asset in dataset');
            if (err && err.error && err.error.code === BrainBankErrorCode.AssetNotFound) {
                console.warn('Fetching first asset in dataset instead');
                return 0;
            } else {
                throw err;
            }
        }
    }

    public async saveSortOrder(datasetIdentifier: string, sortOrder: SortOrder): Promise<void> {
        await this.settingsProvider.mapSet('library.sortOrder', datasetIdentifier, JSON.stringify(sortOrder));
    }

    public async loadSortOrder(datasetIdentifier: string): Promise<SortOrder> {
        await this.ensureDependenciesLoaded([this.settingsProvider]);
        const sortOrder = this.settingsProvider.mapGet('library.sortOrder', datasetIdentifier);

        if (sortOrder) {
            return JSON.parse(sortOrder);
        } else {
            return { sort: 'dateAdded', order: 'asc' };
        }
    }

    private processClassCounts(classCounts: UnprocessedClassCount[]): ProcessedClassCount[] {
        return classCounts
            .filter(cc => {
                return this.classProvider.getClassByID(cc.classIdentifier) !== undefined;
            })
            .map(cc => {
                const classObject = this.classProvider.getClassByID(cc.classIdentifier);
                return {
                    classIdentifier: cc.classIdentifier,
                    numPolygons: cc.numPolygons,
                    color: classObject.color,
                    className: classObject.name
                };
            });
    }

    /**
     * Process a map of classIds to their counts.  There are a couple places
     * in the code that track maps like this.  Process the map and
     * turn it into a ProcessedClassCount array
     *
     */
    public processClassMapCounts(classMap: object): ProcessedClassCount[] {
        const classCounts: UnprocessedClassCount[] = [];
        for (const classId in classMap) {
            classCounts.push({
                classIdentifier: classId,
                numPolygons: classMap[classId]
            });
        }

        return this.processClassCounts(classCounts);
    }

    /**
     * Saves an exported canvas object to BrainBank
     *
     * @params {string} assetID
     * @params {ExportedPolygon[]} polygons
     */
    public async save(assetID: string, polygons: ExportedPolygon[]): Promise<any> {
        if (!assetID) {
            throw new Error('No asset identifier found');
        }

        try {
            console.log('Attempting to save image & polygons', assetID, polygons);
            this.saving = true;
            return await this.api.post(`assets/${assetID}/polygons`, {
                polygon: polygons
            });
        } catch (e) {
            throw e;
        } finally {
            this.saving = false;
        }
    }

    /**
     * Rename an asset
     * @param newName
     * @param assetIdentifier
     */
    public async renameAsset(newName: string, assetIdentifier: string) {
        try {
            await this.api.post(`assets/${assetIdentifier}/rename`, {
                name: newName
            });
        } catch (e) {
            throw e;
        }
    }

    /**
     * lock an asset, if asset already locked, return lock username
     * @param {string} assetIdentifier
     */
    public async lockAsset(assetIdentifier: string): Promise<any> {
        try {
            const resp = await this.api.post(`assets/${assetIdentifier}/lock`, {});
            if (!this.allLockedAssets.includes(assetIdentifier)) {
                this.allLockedAssets.push(assetIdentifier);
            }
            return resp.message;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Attempt to extend the lock on an asset; this will
     * throw an error if the user don't already have the lock
     * @param {string} assetIdentifier
     */
    public async renewAssetLock(assetIdentifier: string): Promise<any> {
        try {
            const resp = await this.api.put(`assets/${assetIdentifier}/lock`, {});
            return resp.message;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Unlock an asset
     * @param {string} assetIdentifier
     */
    public async unlockAsset(assetIdentifier: string): Promise<boolean> {
        try {
            const resp = await this.api.post(`assets/${assetIdentifier}/unlock`);
            // When we're just navigating to a new asset or out of the workspace, remove the
            // unlocked asset from our list, to save time/effort when the user logs out.
            if (resp.success && this.allLockedAssets.includes(assetIdentifier)) {
                this.allLockedAssets.splice(this.allLockedAssets.indexOf(assetIdentifier), 1);
            }
            return resp.success;
        } catch (error) {
            throw error;
        }
    }

    public setUploadModalOpen(modalOpen: boolean): void {
        this.uploadModalOpen = modalOpen;
    }

    public async deleteAsset(assetIdentifier: string, datasetIdentifier: string) {
        try {
            await this.api.delete(`assets/${assetIdentifier}`);
            this.events.publish(EventName.AssetDeleted, {
                assetIdentifier,
                datasetIdentifier
            });
        } catch (e) {
            throw e;
        }
    }

    /**
     * Assume there is a previously worked on image in the storage.  If there is, get the identifier
     * and find out where it is in the dataset.
     *
     * If there is no image data stored, it will assume the index is 0
     */
    public async determineActiveDatasetAsset(datasetIdentifier: string): Promise<number> {
        try {
            const currentAssetID = await this.datasetProvider.getActiveAssetForDataset(datasetIdentifier);

            if (currentAssetID) {
                this.currentAssetIndex = await this.getDatasetOffset(datasetIdentifier, currentAssetID);
            } else {
                this.currentAssetIndex = 0;
            }

            return this.currentAssetIndex;
        } catch (err) {
            throw err;
        }
    }

    /**
     * Get a list of all assets that have been marked as locked this session.
     * Please note this will not include assets that were already locked when a user's session began.
     */
    public getAllLockedAssets() {
        return this.allLockedAssets;
    }

    /** Systemically remove all existing locks from the user's images. */
    public unlockAllLockedAssets() {
        this.allLockedAssets.forEach(asset => this.unlockAsset(asset));
    }

    public getItemsPerPageLimit(itemsPerPage: string | number, total: number): number {
        if (itemsPerPage == SHOW_ALL_LABEL) {
            return total;
        } else {
            return Number(itemsPerPage);
        }
    }

    public incrementCurrentAssetIndex(): void {
        const lastAssetIndex = this.currentAssetIndex - 1;

        // To enable carousel-style / infinite navigation, we need to check whether the user is on the last asset
        // to set the correct asset index
        if (this.currentAssetIndex == lastAssetIndex) {
            this.setCurrentAssetIndex(0);
        } else {
            this.setCurrentAssetIndex(this.currentAssetIndex + 1);
        }
    }

    public decrementCurrentAssetIndex(): void {
        // To enable carousel-style / infinite navigation, we need to check whether the user is on the first asset
        // to set the correct asset index
        if (this.currentAssetIndex == 0) {
            this.setCurrentAssetIndex(this.assetResults.total - 1);
        } else {
            this.setCurrentAssetIndex(this.currentAssetIndex - 1);
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/asset.ts